// ICP import rules for the Log page (IcpLog in LogView.jsx). Pure, so the
// rules have tests.
import { implausibleReading } from '../../shared/reading-plausibility.js';
import { formatSalinity, salinityUnitLabel, salinityToPpt } from './salinity.js';
import { formatTemp } from './units.js';
import { FRESHWATER_SUBTYPES } from '../../shared/param-explainers.js';
import { isFreshwater } from '../../shared/sensei-action.js';

// When the sample was taken, from the keeper's LOCAL calendar day: noon of
// that day where they are, or now when that noon has not come yet. Noon
// UTC put yesterday in the future for a keeper in New Zealand just after
// midnight, and the reading was refused. Null for a date that is not a
// date or is before 2000 (the server refuses those too).
export function icpSampleTs(dateStr, now = Date.now()) {
  if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  if (dateStr < '2000-01-01') return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const noon = new Date(y, m - 1, d, 12, 0, 0, 0).getTime();
  if (!Number.isFinite(noon)) return null;
  return new Date(Math.min(noon, now)).toISOString();
}

// A major (salinity, alkalinity, calcium, magnesium, sodium, potassium) with
// no number (ATI prints "Sal. total ---") is a figure the lab did not report:
// no seawater is below detection on those. Fresh water, or a printed limit
// ("<0.5"), is a result. The same rule as the results page
// (unreportedMajor in server/icp-analysis.js).
export function icpRowNotReported(row, tank) {
  if (!row?.no_number_major || row.detection_limit != null) return false;
  return !isFreshwater(tank);
}

// A mapped row the tank can take as a reading. icpImportPlan refuses one
// whose card the tank lacks, so such a row starts unticked: a freshwater
// ICP's calcium and magnesium used to refuse the whole import.
export function icpRowHasCard(row, tank) {
  if (!row?.mapped_param) return false;
  const params = tank?.params && Object.keys(tank.params).length ? tank.params : null;
  return !params || !!params[row.mapped_param];
}

export function icpDefaultSelection(rows, tank) {
  const sel = {};
  for (const row of rows || []) if (icpRowHasCard(row, tank)) sel[row.mapped_param] = true;
  return sel;
}

// "Save the panel only" (owner, 2026-10-17): with no daily reading ticked a
// report is still kept on the ICP page, when the lab is one we read and a
// row carries a number. An unknown lab keeps its "send us this report" offer.
export function icpPanelSavable(preview, tank) {
  if (!preview?.lab || preview.lab === 'Unknown') return false;
  return (preview.rows || []).some((r) => {
    const v = r?.value;
    if (v == null || v === '' || !Number.isFinite(Number(v))) return false;
    return !icpRowNotReported(r, tank);
  });
}

// The readings an import will save, checked BEFORE the first one is sent.
// One save the server refused (a salinity no meter reads, a parameter the
// tank has no card for) used to stop the loop halfway with the rest
// already saved, and tapping Import again saved those twice. `saved` holds
// the keys of rows already saved from this report, so a retry skips them.
// The key carries the sample DATE, not ts: for a sample taken this morning
// ts is "now", which moves between two taps, so the retry saved them again.
export function icpImportPlan(rows, selected, { tank, ts, sampleDate, saved = new Set() } = {}) {
  const toSave = [];
  const problems = [];
  const params = tank?.params && Object.keys(tank.params).length ? tank.params : null;
  const fresh = FRESHWATER_SUBTYPES.includes(String(tank?.subtype_slug || ''));
  for (const r of rows || []) {
    if (!r?.mapped_param || !selected?.[r.mapped_param]) continue;
    const param = r.mapped_param;
    const value = Number(r.mapped_value ?? r.value);
    const unit = r.mapped_unit || r.unit || null;
    const key = `${param}|${sampleDate || ts}|${value}`;
    if (saved.has(key)) continue;
    if (!Number.isFinite(value)) {
      problems.push({ label: r.label, message: 'the report gives no number for it.' });
      continue;
    }
    if (params && !params[param]) {
      problems.push({ label: r.label, message: 'this tank has no card for it.' });
      continue;
    }
    // The server stores salinity in ppt and converts an S.G. number first;
    // the check below must judge the number it will judge.
    const checked =
      param === 'salinity' && !fresh && value >= 1 && value <= 1.04 ? salinityToPpt(value) : value;
    const bad = implausibleReading(param, checked, { subtypeSlug: tank?.subtype_slug });
    if (bad) {
      problems.push({ label: r.label, message: bad.message });
      continue;
    }
    toSave.push({ param, value, unit, key });
  }
  return { toSave, problems };
}

// One line for the keeper, naming every row that would not save.
export function icpProblemText(problems) {
  if (!problems?.length) return '';
  const list = problems.map((p) => `${p.label}: ${p.message}`).join(' ');
  return `Nothing was saved yet. ${list} Untick ${problems.length === 1 ? 'it' : 'them'} to import the rest.`;
}

// The value column of the preview, in the keeper's units. A salinity is
// stored in ppt, and "35.151 ppt (was 35.151 PSU)" was shown to a keeper
// who reads S.G.; the lab's own print stays beside it when it differs.
export function icpPreviewFigure(row, { salinityUnit = 'ppt', tempUnit = 'F' } = {}) {
  const lab = row?.value != null ? `${row.value}${row.unit ? ` ${row.unit}` : ''}` : '';
  const mapped = row?.mapped_value ?? null;
  if (!row?.mapped_param || mapped == null || !Number.isFinite(Number(mapped))) {
    return { main: lab, lab: null };
  }
  let main;
  if (row.mapped_param === 'salinity') {
    main = `${formatSalinity(mapped, salinityUnit)} ${salinityUnitLabel(salinityUnit)}`;
  } else if (row.mapped_param === 'temp') {
    main = formatTemp(mapped, tempUnit);
  } else {
    main = `${mapped}${row.mapped_unit ? ` ${row.mapped_unit}` : ''}`;
  }
  return { main, lab: main === lab ? null : lab };
}

// "target 1.026 S.G." / "target 25.0 °C" for the row's card.
export function icpTargetText(meta, param, { salinityUnit = 'ppt', tempUnit = 'F' } = {}) {
  if (meta?.target == null || meta.target === '') return '';
  if (param === 'salinity') {
    return `target ${formatSalinity(meta.target, salinityUnit)} ${salinityUnitLabel(salinityUnit)}`;
  }
  if (param === 'temp') return `target ${formatTemp(meta.target, tempUnit)}`;
  return `target ${meta.target}${meta.unit ? ` ${meta.unit}` : ''}`;
}
